import { differenceInSeconds, parse } from 'date-fns';
import { type TFunction } from 'i18next';

const SECONDS_PER_MINUTE = 60;
const SECONDS_PER_HOUR = 60 * 60;
const SECONDS_PER_DAY = 60 * 60 * 24;

// 남은 사용기한을 일수와 시간수로 보여줍니다. 예시: "1일 2시간 남음"
export const getRemainingTime = (
  expirationDate: string,
  t: TFunction<'billing'>,
): string => {
  const now = new Date();
  const expiry = parse(expirationDate, 'yyyy. M. d. HH:mm', new Date());
  const diffInSeconds = differenceInSeconds(expiry, now);

  if (diffInSeconds <= 0) {
    return t('voucher.remaining.expired');
  }

  const diffMinutes = Math.floor(
    (diffInSeconds % SECONDS_PER_HOUR) / SECONDS_PER_MINUTE,
  );
  const diffHours = Math.floor(
    (diffInSeconds % SECONDS_PER_DAY) / SECONDS_PER_HOUR,
  );
  const diffDays = Math.floor(diffInSeconds / SECONDS_PER_DAY);

  // 1분 미만 (60초 미만)
  if (diffInSeconds < SECONDS_PER_MINUTE) {
    return t('voucher.remaining.lessThanMinute');
  }

  // 1시간 미만 (3600초 미만) - 분 단위로 표시
  if (diffInSeconds < SECONDS_PER_HOUR) {
    return t('voucher.remaining.minutes', { minutes: String(diffMinutes) });
  }

  // 1일 미만 (86400초 미만) - 시간 단위로 표시 (분은 표시하지 않음)
  if (diffInSeconds < SECONDS_PER_DAY) {
    return t('voucher.remaining.hours', { hours: String(diffHours) });
  }

  // 1일 이상 (일수와 시간수로 표시)
  const remainingHours = diffHours % 24;
  if (remainingHours > 0) {
    return t('voucher.remaining.daysHours', {
      days: String(diffDays),
      hours: String(remainingHours),
    });
  }

  return t('voucher.remaining.days', { days: String(diffDays) });
};

// 타임존에 맞게 date string을 포맷팅합니다
export const formatDateByTimezone = (
  dateString: string,
  timezone: string,
): string => {
  return new Date(dateString).toLocaleString('ko-KR', {
    hour12: false,
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    timeZone: timezone,
  });
};
